/* Patch selector - keeps the tile highlight and the hidden per-position fields in
   sync with what the customer picked.

   ONE code path now. There used to be two - radios for single-badge products and
   checkboxes for Inter and Chelsea - because several choices had to be squeezed into
   one `properties[Patch]`. Each position on the shirt owns its own property today, so
   every product is a set of radio groups and the modes collapsed into each other.

   What this script is NOT responsible for: getting the customer's choice onto the
   order. The radios are real form fields inside the product form (via `form=`), so
   the Hebrew label submits whether or not this file loads. That was the whole point
   of the rewrite - the previous multi markup had no `name` at all and depended on
   this script to enable disabled hidden fields, so a script that failed to load left
   a picker that lit up on click and submitted nothing.

   Without this script the customer loses the English supplier spec and the badge
   image on the order line. futbolista-orders-tool covers the first from the generated
   label table (data/patch-labels.json), so the factory still gets full English.

   Built on the same footing as version-selector.js: an IIFE, no framework, no build
   step. */

(function () {
  'use strict';

  /* Pair lock: the two World Cup sleeves are worn together.
     Every 2026 World Cup match shirt carried the round badge on the LEFT sleeve and
     the tournament badge on the RIGHT one, so a shirt with only one of the two is a
     combination that never existed on the pitch. Picking either one picks the other;
     clearing either one clears the other.

     Two rules, because the directions are not symmetric:

       ON   pulls the partner on only when the partner offers exactly one choice. With
            8 stages there is nothing in the click that says which one was meant.
       OFF  always clears the partner, whatever it offers. Nothing to infer.

     An earlier version required the single-choice test on BOTH directions, and the
     shape of a real product made that a hole rather than a nicety: a World Cup shirt
     has 8 stages and 1 badge, so clearing the badge found 8 partners, inferred
     nothing, and left the stage selected on its own.

     That still left the same illegal state reachable from the other end - click the
     badge first and it comes on with no stage - so the badge no longer takes clicks at
     all. `markAutoPair` marks the follower position and the handlers skip it; the note
     under it already told the customer it "joins automatically", and now it does only
     that. The marking is applied from script, so with JS off the group stays clickable
     and both halves remain reachable by hand.

     Positions outside this pair (`sleeve`, `chest`, `cup`, `champion`,
     `league-champion`, `copa-champion`) are never consulted, which is what keeps every club product
     behaving exactly as it does today. */
  var PAIR = { 'sleeve-left': 'sleeve-right', 'sleeve-right': 'sleeve-left' };

  /* Rival sets: two complete sleeve setups that can never be worn together.
     A European national-team shirt is sold with its World Cup pair (stage on the left,
     tournament badge on the right) AND with the Nations League set, which is one
     `sleeve` patch covering both sleeves. They are alternatives, not additions - a
     shirt with a World Cup stage patch next to the Nations League flag never existed.

     So a real pick in one set sends the other set back to its "no patch" tile. Only a
     REAL pick does: choosing "no patch" in one set says nothing about the other.

     This is inert on every product that does not carry both shapes. On a club shirt
     there is no `sleeve-left` group and on a World Cup shirt without the Nations
     League there is no `sleeve` group, so `noneOf` finds nothing and nothing moves.
     Measured 2026-09-27: of the 351 products carrying patches, zero carry both. */
  /* `copa-champion` (the Copa America winners patch, centre chest) is the second
     "other set": on an Argentina shirt it takes the place the Nations League set has on
     a European one, and is exactly as exclusive against the World Cup pair. It is
     sewn on the chest, so nothing physical stops it sitting beside the sleeve
     badges; the either/or is Gal's layout decision of 2026-10-04, same as the
     Nations League card. */
  var RIVALS = {
    'sleeve': ['sleeve-left', 'sleeve-right'],
    'copa-champion': ['sleeve-left', 'sleeve-right'],
    'sleeve-left': ['sleeve', 'copa-champion'],
    'sleeve-right': ['sleeve', 'copa-champion']
  };

  function inputsIn(root, group) {
    return root.querySelectorAll('input[data-patch-group="' + group + '"]');
  }

  /* The real options in a position, excluding its "no patch" tile - that tile is the
     empty state, not a choice, and counting it would make every single-option group
     look like it had two. */
  function soleMember(root, group) {
    var all = root.querySelectorAll(
      'input[data-patch-group="' + group + '"]:not([data-patch-none])'
    );
    return all.length === 1 ? all[0] : null;
  }

  function noneOf(root, group) {
    return root.querySelector('input[data-patch-group="' + group + '"][data-patch-none]');
  }

  /* Dependency lock: `data-patch-requires` names the handle of a patch that has to be
     picked for this one to be legal. The La Liga champion bar is sewn on the sleeve
     ABOVE the league badge, so it cannot exist without it - and it cannot sit next to
     a European badge, because that badge REPLACES the league one on the same sleeve.

     This is a different shape from PAIR above and cannot reuse it. PAIR couples two
     whole positions; this couples one option to one specific VALUE inside another
     position, where the other options in that position are the forbidden ones.

     The inline CSS the snippet emits already stops an unmet tile being clicked, with
     no script involved. What CSS cannot do is uncheck a radio, so the direction that
     matters here is the one CSS cannot reach: the requirement is taken away while the
     dependent is already on. */
  function requirementMet(root, handle) {
    return !!root.querySelector('input[data-patch-handle="' + handle + '"]:checked');
  }

  /* Returns the groups it moved, so the caller re-syncs their hidden fields too.
     Assigning .checked from script fires no `change`, so this cannot recurse. */
  function enforceRequires(root, input) {
    var touched = [];

    /* ON: picking a dependent pulls its requirement on with it. Unambiguous by
       construction - `requires` names one handle, so there is nothing to infer. */
    var need = input.getAttribute('data-patch-requires');
    if (need && input.checked && !input.hasAttribute('data-patch-none')) {
      var req = root.querySelector('input[data-patch-handle="' + need + '"]');
      if (req && !req.checked) {
        req.checked = true;
        touched.push(req.getAttribute('data-patch-group'));
      }
    }

    /* OFF: any change anywhere can invalidate a dependent, so every one of them is
       re-tested rather than only the partner of whatever moved. Switching the sleeve
       from La Liga to the Champions League is exactly this path. */
    root.querySelectorAll('input[data-patch-requires]').forEach(function (dep) {
      if (!dep.checked) return;
      if (requirementMet(root, dep.getAttribute('data-patch-requires'))) return;
      var group = dep.getAttribute('data-patch-group');
      var none = noneOf(root, group);
      if (none) {
        none.checked = true;
        touched.push(group);
      }
    });

    return touched;
  }

  /* Mirrors the CSS lock into the accessibility tree. The CSS already takes the tile
     out of the tab order via visibility, so this is what a screen reader reads, not a
     second enforcement path. The radio is never `disabled`: a disabled field submits
     nothing, and the badge would go MISSING from the factory sheet rather than merely
     being unpickable - the same reasoning as markAutoPair. */
  function syncLocks(root) {
    root.querySelectorAll('input[data-patch-requires]').forEach(function (dep) {
      var label = dep.closest('.patch-option');
      if (!label) return;
      var met = requirementMet(root, dep.getAttribute('data-patch-requires'));
      label.classList.toggle('is-locked', !met);
      label.setAttribute('aria-disabled', met ? 'false' : 'true');
    });
  }

  /* Returns the partner's group name when one was moved, so the caller knows it has a
     second group to re-sync. Assigning .checked from script fires no `change` event,
     so this cannot recurse back through the handler.

     The two directions are NOT symmetric, and treating them as one rule was the bug.

     Turning a position ON can only pull its partner on when the partner offers exactly
     one choice: a World Cup shirt carries 8 stages, and there is nothing in a click on
     the tournament badge that says which of them the customer meant.

     Turning a position OFF always turns the partner off, with no such condition. There
     is nothing to infer here - the pair is worn together, so neither half survives
     alone, whatever the partner offers. Requiring `soleMember` on this side too is what
     let "clear the tournament badge" leave a stage patch selected on its own: an order
     for a combination that never existed on the pitch. */
  function enforcePair(root, input) {
    var group = input.getAttribute('data-patch-group');
    var partnerGroup = group && PAIR[group];
    if (!partnerGroup) return null;

    if (!input.hasAttribute('data-patch-none')) {
      var partner = soleMember(root, partnerGroup);
      if (!partner) return null;
      partner.checked = true;
      return partnerGroup;
    }

    var partnerNone = noneOf(root, partnerGroup);
    if (!partnerNone) return null;
    partnerNone.checked = true;
    return partnerGroup;
  }

  /* Returns the groups it moved, so the caller re-syncs their hidden fields too.
     Assigning .checked from script fires no `change`, so this cannot recurse. Runs
     before enforceRequires, so a dependent whose requirement it just cleared is
     caught by that pass in the same click. */
  function enforceRivals(root, input) {
    var touched = [];
    if (input.hasAttribute('data-patch-none')) return touched;
    var rivals = RIVALS[input.getAttribute('data-patch-group')] || [];
    rivals.forEach(function (group) {
      var none = noneOf(root, group);
      if (none && !none.checked) {
        none.checked = true;
        touched.push(group);
      }
    });
    return touched;
  }

  /* The click path above only guards transitions, so a state the page ARRIVES with is
     never tested by it. A browser that restores form fields (Firefox on reload or Back,
     autofill extensions) restores each radio group on its own, and can hand the page a
     Nations League pick and a World Cup stage at once. Once this script boots the no-JS
     CSS lock stops applying, so without this sweep that pair would ship untouched.

     Nothing in the restored state says which of the two the customer meant, so both
     sets go back to "no patch" rather than one being kept by guess. A customer who
     lands here re-picks; a factory never receives a shirt that cannot exist. */
  function realPick(root, group) {
    return root.querySelector('input[data-patch-group="' + group + '"]:not([data-patch-none]):checked');
  }

  function reconcileRivals(root) {
    if (!realPick(root, 'sleeve') && !realPick(root, 'copa-champion')) return;
    if (!realPick(root, 'sleeve-left') && !realPick(root, 'sleeve-right')) return;
    ['sleeve', 'copa-champion', 'sleeve-left', 'sleeve-right'].forEach(function (group) {
      var none = noneOf(root, group);
      if (none) none.checked = true;
    });
  }

  /* Set state (rival-mode products only): which set holds the current pick - none,
     World Cup, Nations League, or Copa America winners. Only "none" is drawn, as the tile above both set
     cards; the other two values exist so the state is always one of three, which is
     what lights the top tile when nothing is picked and turns it off otherwise.
     `data-patch-set-active` mirrors it on the root for styling and for tests.

     Picking the top tile clears both sets. applySet also handles `wc` and `unl`,
     which no control sends today: kept so a future switch is a markup change only. */
  function switchOf(root, value) {
    return root.querySelector('input[data-patch-set-switch="' + value + '"]');
  }

  function showSet(root, value) {
    var input = switchOf(root, value);
    if (!input) return;
    input.checked = true;
    root.setAttribute('data-patch-set-active', value);
  }

  /* Read the switch back from what is actually picked, for every path that changes
     picks without going through the switch: boot, restore, clear-all, and a tile
     toggled off. */
  function deriveSet(root) {
    if (!switchOf(root, 'none')) return;
    if (realPick(root, 'sleeve')) showSet(root, 'unl');
    else if (realPick(root, 'copa-champion')) showSet(root, 'copa');
    else if (realPick(root, 'sleeve-left') || realPick(root, 'sleeve-right')) showSet(root, 'wc');
    else showSet(root, 'none');
  }

  function applySet(root, value) {
    var clear = value === 'wc' ? ['sleeve', 'copa-champion']
      : value === 'unl' || value === 'copa' ? ['sleeve-left', 'sleeve-right']
      : ['sleeve', 'copa-champion', 'sleeve-left', 'sleeve-right'];
    clear.forEach(function (group) {
      var none = noneOf(root, group);
      if (none) none.checked = true;
    });
    if (value === 'unl' || value === 'copa') {
      var only = soleMember(root, value === 'copa' ? 'copa-champion' : 'sleeve');
      if (only) only.checked = true;
    }
    root.setAttribute('data-patch-set-active', value);
    syncAll(root);
  }

  /* Writes the two hidden fields for ONE position. Keyed by position, never by DOM
     order: the old code filled three image slots positionally, so a fourth choice
     vanished from the images while still appearing in the text - an order whose name
     said four badges and whose pictures showed three. */
  function syncGroup(root, group) {
    var all = inputsIn(root, group);
    var checked = root.querySelector('input[data-patch-group="' + group + '"]:checked');
    var enField = root.querySelector('[data-patch-en-field="' + group + '"]');
    var imgField = root.querySelector('[data-patch-image-field="' + group + '"]');

    all.forEach(function (input) {
      var label = input.closest('.patch-option');
      if (label) label.classList.toggle('is-selected', input.checked);
    });

    if (enField) enField.value = checked ? checked.getAttribute('data-patch-en') || '' : '';
    if (imgField) imgField.value = checked ? checked.getAttribute('data-patch-image') || '' : '';
  }

  /* The hidden English field is emitted once per position, so it is also the list of
     positions this product offers - one source, not a second list that could disagree
     with the markup around it. */
  function groupsIn(root) {
    var out = [];
    root.querySelectorAll('[data-patch-en-field]').forEach(function (f) {
      out.push(f.getAttribute('data-patch-en-field'));
    });
    return out;
  }

  function syncAll(root) {
    groupsIn(root).forEach(function (group) { syncGroup(root, group); });
    syncLocks(root);
  }

  /* One entry point for "this input is now the choice", whether the browser checked it
     or we did. Keeps the pair, the dependency, the highlight and the hidden fields from
     ever disagreeing about what was picked. */
  function applyFrom(root, input) {
    var partnerGroup = enforcePair(root, input);
    var rivalGroups = enforceRivals(root, input);
    var requireGroups = enforceRequires(root, input);
    syncGroup(root, input.getAttribute('data-patch-group'));
    if (partnerGroup) syncGroup(root, partnerGroup);
    rivalGroups.forEach(function (group) { syncGroup(root, group); });
    requireGroups.forEach(function (group) { syncGroup(root, group); });
    syncLocks(root);
    deriveSet(root);
  }

  function clearGroup(root, group) {
    var none = noneOf(root, group);
    if (!none) return;
    none.checked = true;
    applyFrom(root, none);
  }

  /* Clear-all, wired to the button the snippet renders for multi-position products
     only. Every group goes to its own empty tile, then one sync pass - going through
     applyFrom per group would make the pair lock fight the clear it is part of. */
  function clearAll(root) {
    groupsIn(root).forEach(function (group) {
      var none = noneOf(root, group);
      if (none) none.checked = true;
    });
    syncAll(root);
    deriveSet(root);
  }

  /* An element inside a position the customer does not drive. */
  function inAutoGroup(el) {
    return !!(el && el.closest && el.closest('[data-patch-auto]'));
  }

  /* A tile whose requirement is not met. The CSS lock already stops the pointer and
     takes the radio out of the tab order, so this is the backstop for any path that
     reaches a handler anyway - a synthetic event, or a browser where `:has` did not
     apply. Cheap, and it keeps the three handlers from each needing their own test. */
  function isLocked(el) {
    return !!(el && el.closest && el.closest('.patch-option.is-locked'));
  }

  /* Mark the half of the pair that only ever follows the other one.
     On a World Cup shirt the tournament badge is one fixed colourway and the stage is a
     choice among eight, so the arrow points one way only: pick a stage and the badge
     comes with it. Leaving the badge clickable let a customer switch it on by itself,
     which is the same forbidden "one without the other" as clearing it and stranding a
     stage - just reached from the other end.

     Marked from JS and never from Liquid, deliberately. Without JS the pair lock does
     not run at all, so the customer has to be able to work both halves by hand; the
     attribute simply never appears there and the group stays fully interactive. And the
     radio is never `disabled`, because a disabled field submits nothing and the badge
     would vanish from the factory sheet rather than merely being awkward to pick.

     A shirt whose two positions each offer one choice is NOT marked: there the pair
     already locks in both directions, so nothing needs taking away. Neither is a shirt
     carrying two tournament colourways - there the badge is a real decision. */
  function markAutoPair(root) {
    Object.keys(PAIR).forEach(function (group) {
      var only = soleMember(root, group);
      if (!only) return;
      var partners = root.querySelectorAll(
        'input[data-patch-group="' + PAIR[group] + '"]:not([data-patch-none])'
      );
      if (partners.length < 2) return;

      var box = only.closest('fieldset') || only.closest('.patch-group');
      if (!box) return;
      box.setAttribute('data-patch-auto', '');
      inputsIn(root, group).forEach(function (el) { el.setAttribute('tabindex', '-1'); });
    });
  }

  function init(root) {
    if (root.dataset.patchSelectorReady === 'true') return;
    root.dataset.patchSelectorReady = 'true';

    markAutoPair(root);

    root.addEventListener('change', function (event) {
      var t = event.target;
      if (t && t.hasAttribute && t.hasAttribute('data-patch-set-switch')) {
        applySet(root, t.value);
        return;
      }
      if (!t || !t.hasAttribute || !t.hasAttribute('data-patch-group')) return;
      applyFrom(root, t);
    });

    // Clicking the tile you already picked clears that position, back to "no patch".
    //
    // A radio cannot be unchecked by clicking it, and clicking one that is already
    // checked fires NO `change` event at all - so the listener above never runs and
    // the picker looks stuck. The state therefore has to be read BEFORE the browser
    // processes the click: pointerdown records it, click acts on it. pointerdown also
    // covers touch and pen, which a mousedown handler would miss on a phone.
    root.addEventListener('pointerdown', function (event) {
      var label = event.target.closest('.patch-option');
      if (!label || inAutoGroup(label) || isLocked(label)) return;
      var input = label.querySelector('input[data-patch-group]');
      if (input) input.dataset.wasChecked = input.checked ? '1' : '';
    });

    root.addEventListener('click', function (event) {
      var label = event.target.closest('.patch-option');
      if (!label || inAutoGroup(label) || isLocked(label)) return;
      var input = label.querySelector('input[data-patch-group]');
      // The "no patch" tile IS the cleared state, so it is never a toggle - clicking
      // it twice must leave it selected, not blank the row.
      if (!input || input.hasAttribute('data-patch-none')) return;
      if (input.dataset.wasChecked !== '1') return;
      // preventDefault is what makes this work, and it took a measurement to find.
      // Clearing alone was silently undone: a <label> wrapping an input forwards a
      // SECOND click to that input as its activation behaviour, and that re-checked
      // the radio right after the clear. Logged order on a repeat click was
      // pointerdown(IMG) -> click(IMG, cleared here) -> click(INPUT, re-checked) ->
      // change -> highlight restored. Cancelling the default action stops the forward.
      event.preventDefault();
      clearGroup(root, input.getAttribute('data-patch-group'));
      deriveSet(root);
      // The flag is dropped so that if a browser ever forwards anyway, the second pass
      // is a no-op rather than depending on clearGroup being harmless twice.
      input.dataset.wasChecked = '';
    });

    // Space/Enter on a focused label should behave like a click. The label wraps the
    // radio, so the browser already handles Space; this covers Enter, which it does
    // not. Enter toggles for the same reason the mouse does - a keyboard user who
    // cannot clear a choice the way a mouse user can is a worse bug than the one
    // being fixed.
    root.addEventListener('keydown', function (event) {
      if (event.key !== 'Enter') return;
      var label = event.target.closest('.patch-option');
      if (!label || inAutoGroup(label) || isLocked(label)) return;
      var input = label.querySelector('input[data-patch-group]');
      if (!input) return;
      event.preventDefault();
      if (input.checked && !input.hasAttribute('data-patch-none')) {
        clearGroup(root, input.getAttribute('data-patch-group'));
        deriveSet(root);
      } else {
        input.checked = true;
        applyFrom(root, input);
      }
    });

    var clearBtn = root.querySelector('[data-patch-clear-all]');
    if (clearBtn) {
      clearBtn.addEventListener('click', function () { clearAll(root); });
    }

    reconcileRivals(root);
    syncAll(root);
    deriveSet(root);
  }

  function boot() {
    document.querySelectorAll('[data-patch-selector]').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // Theme Editor re-renders the section on every setting change. This theme does NOT
  // re-render the product form on variant change (the picker only rewrites a hidden
  // id input), so this event is the only re-init path actually needed - a
  // MutationObserver over the document would cost real work on every DOM change to
  // catch a case that does not occur.
  document.addEventListener('shopify:section:load', boot);

  // Restoration can also land after boot (a page brought back from the back-forward
  // cache fires no DOMContentLoaded), so the same sweep runs on every pageshow.
  window.addEventListener('pageshow', function () {
    document.querySelectorAll('[data-patch-selector]').forEach(function (root) {
      reconcileRivals(root);
      syncAll(root);
      deriveSet(root);
    });
  });
})();
